import { useState, useCallback } from 'react';
import { Position } from '@/utils/tree/types';

interface UseTreeNavigationOptions {
  minScale?: number;
  maxScale?: number;
  containerDimensions: { width: number; height: number };
}

export function useTreeNavigation({ 
  minScale = 0.3, 
  maxScale = 2,
  containerDimensions
}: UseTreeNavigationOptions) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ 
      x: e.clientX - position.x, 
      y: e.clientY - position.y 
    });
  }, [position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.min(Math.max(scale * zoomFactor, minScale), maxScale);
    
    // Calculate zoom center point
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Calculate point before zoom
    const pointXBeforeZoom = (mouseX - position.x) / scale;
    const pointYBeforeZoom = (mouseY - position.y) / scale;
    
    setScale(newScale);
    setPosition({
      x: mouseX - (pointXBeforeZoom * newScale),
      y: mouseY - (pointYBeforeZoom * newScale)
    });
  }, [scale, position, minScale, maxScale]);

  const handleButtonZoom = useCallback((zoomIn: boolean) => {
    const zoomFactor = zoomIn ? 1.1 : 0.9;
    const newScale = Math.min(Math.max(scale * zoomFactor, minScale), maxScale);

    // Zoom from center of container
    const centerX = containerDimensions.width / 2;
    const centerY = containerDimensions.height / 2;
    
    const pointXBeforeZoom = (centerX - position.x) / scale;
    const pointYBeforeZoom = (centerY - position.y) / scale;

    setScale(newScale);
    setPosition({
      x: centerX - (pointXBeforeZoom * newScale),
      y: centerY - (pointYBeforeZoom * newScale)
    });
  }, [scale, position, minScale, maxScale, containerDimensions]);

  const resetView = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  return {
    scale,
    position,
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    handleButtonZoom,
    resetView
  };
}